import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { BarChart3, RefreshCw, Palette, Download } from 'lucide-react';
import { toast } from 'sonner';

// Import graph components
import TemporalGraph from './graphs/TemporalGraph';
import SpectrumGraph from './graphs/SpectrumGraph';
import HistogramGraph from './graphs/HistogramGraph';
import ComparativeGraph from './graphs/ComparativeGraph';
import HeatmapGraph from './graphs/HeatmapGraph';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvancedGraphs = ({ soundmeters }) => {
  const [selectedSoundmeter, setSelectedSoundmeter] = useState('');
  const [selectedSoundmeters, setSelectedSoundmeters] = useState([]);
  const [chartType, setChartType] = useState('temporal');
  const [weighting, setWeighting] = useState('dB_A');
  const [colorScheme, setColorScheme] = useState('default');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('24');

  useEffect(() => {
    if (soundmeters && soundmeters.length > 0 && !selectedSoundmeter) {
      setSelectedSoundmeter(soundmeters[0].soundmeter_id);
    }
  }, [soundmeters, selectedSoundmeter]);

  const loadChartData = async () => {
    if (chartType === 'comparative' && selectedSoundmeters.length < 2) {
      toast.error("Sélectionnez au moins 2 sonomètres pour la comparaison");
      return;
    }

    if (!selectedSoundmeter && chartType !== 'comparative') {
      toast.error("Sélectionnez un sonomètre");
      return;
    }

    setLoading(true);
    try {
      let endpoint = "";
      let params = new URLSearchParams();

      switch(chartType) {
        case "temporal":
          endpoint = `/charts/temporal/${selectedSoundmeter}`;
          params.append('weighting_type', weighting);
          params.append('hours', timeRange);
          break;
        
        case "spectrum":
          endpoint = `/charts/spectrum/${selectedSoundmeter}`;
          break;
        
        case "histogram":
          endpoint = `/charts/histogram/${selectedSoundmeter}`;
          params.append('weighting_type', weighting);
          params.append('hours', timeRange);
          break;
        
        case "heatmap":
          endpoint = `/charts/temporal/${selectedSoundmeter}`;
          params.append('weighting_type', weighting);
          params.append('hours', '168'); // 7 days for heatmap
          break;
        
        case "comparative":
          // For comparative, we need to fetch data for multiple soundmeters
          const comparativeData = {};
          for (const smId of selectedSoundmeters) {
            const response = await axios.get(`${API}/charts/temporal/${smId}?weighting_type=${weighting}&hours=${timeRange}`);
            comparativeData[smId] = response.data;
          }
          setChartData(comparativeData);
          setLoading(false);
          return;
        
        default:
          toast.error("Type de graphique non supporté");
          setLoading(false);
          return;
      }

      if (endpoint) {
        const response = await axios.get(`${API}${endpoint}?${params}`);
        setChartData(response.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du graphique");
      console.error(error);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSoundmeterToggle = (soundmeterId) => {
    setSelectedSoundmeters(prev => {
      if (prev.includes(soundmeterId)) {
        return prev.filter(id => id !== soundmeterId);
      } else {
        return [...prev, soundmeterId];
      }
    });
  };

  const exportChartData = () => {
    if (!chartData) {
      toast.error("Aucune donnée à exporter");
      return;
    }

    const dataStr = JSON.stringify(chartData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tkale_${chartType}_${selectedSoundmeter}_${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success("Données exportées avec succès");
  };

  const renderChart = () => {
    if (!chartData) {
      return (
        <div className="flex items-center justify-center h-96 text-slate-500">
          <div className="text-center">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <p>Sélectionnez les paramètres et cliquez sur "Générer" pour afficher le graphique</p>
          </div>
        </div>
      );
    }

    switch(chartType) {
      case "temporal":
        return (
          <TemporalGraph 
            data={chartData}
            soundmeterId={selectedSoundmeter}
            weighting={weighting}
            colorScheme={colorScheme}
          />
        );
      
      case "spectrum":
        return (
          <SpectrumGraph 
            data={chartData}
            soundmeterId={selectedSoundmeter}
            colorScheme={colorScheme}
          />
        );
      
      case "histogram":
        return (
          <HistogramGraph 
            data={chartData}
            soundmeterId={selectedSoundmeter}
            weighting={weighting}
            colorScheme={colorScheme}
          />
        );
      
      case "heatmap":
        return (
          <HeatmapGraph 
            data={chartData}
            soundmeterId={selectedSoundmeter}
            weighting={weighting}
            colorScheme={colorScheme}
          />
        );
      
      case "comparative":
        return (
          <ComparativeGraph 
            data={chartData}
            soundmeterIds={selectedSoundmeters}
            weighting={weighting}
            colorScheme={colorScheme}
          />
        );
      
      default:
        return (
          <div className="text-center py-8 text-slate-500">
            Type de graphique non reconnu
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Configuration des Graphiques</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Chart Type Selection */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Label>Type de graphique</Label>
              <Select value={chartType} onValueChange={(value) => {
                setChartType(value);
                setChartData(null);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="temporal">📈 Évolution Temporelle</SelectItem>
                  <SelectItem value="spectrum">📊 Spectre Fréquentiel</SelectItem>
                  <SelectItem value="histogram">📉 Histogramme</SelectItem>
                  <SelectItem value="comparative">📊 Comparaison Multi-Sites</SelectItem>
                  <SelectItem value="heatmap">🔥 Carte de Chaleur</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {chartType !== 'comparative' && (
              <div>
                <Label>Sonomètre(s)</Label>
                  <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
      {soundmeters?.map(sm => (
        <div key={sm.soundmeter_id} className="flex items-center space-x-2">
          <input
            type="checkbox"
            id={sm.soundmeter_id}
            checked={selectedSoundmeters.includes(sm.soundmeter_id)}
            onChange={() => handleSoundmeterToggle(sm.soundmeter_id)}
            className="cursor-pointer"
          />
          <label htmlFor={sm.soundmeter_id} className="text-sm cursor-pointer">
            {sm.name || sm.soundmeter_id}
          </label>
        </div>
      ))}
    </div>
  </div>
)}


            {chartType !== 'spectrum' && (
              <div>
                <Label>Pondération</Label>
                <Select value={weighting} onValueChange={setWeighting}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dB_A">dB(A)</SelectItem>
                    <SelectItem value="dB_C">dB(C)</SelectItem>
                    <SelectItem value="dB_L">dB(L)</SelectItem>
                    <SelectItem value="dB_Z">dB(Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {chartType !== 'heatmap' && chartType !== 'spectrum' && (
              <div>
                <Label>Période</Label>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 heures</SelectItem>
                    <SelectItem value="12">12 heures</SelectItem>
                    <SelectItem value="24">24 heures</SelectItem>
                    <SelectItem value="48">48 heures</SelectItem>
                    <SelectItem value="168">7 jours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Comparative Mode: Multiple Soundmeter Selection */}
          {chartType === 'comparative' && (
            <div>
              <Label className="mb-2 block">Sélection des sonomètres à comparer</Label>
              <div className="flex flex-wrap gap-2">
                {soundmeters?.map(sm => (
                  <Badge
                    key={sm.soundmeter_id}
                    variant={selectedSoundmeters.includes(sm.soundmeter_id) ? 'default' : 'outline'}
                    className="cursor-pointer px-3 py-2"
                    onClick={() => handleSoundmeterToggle(sm.soundmeter_id)}
                  >
                    {sm.name || sm.soundmeter_id}
                    {selectedSoundmeters.includes(sm.soundmeter_id) && ' ✓'}
                  </Badge>
                ))}
              </div>
              {selectedSoundmeters.length > 0 && (
                <div className="mt-2 text-sm text-slate-600">
                  {selectedSoundmeters.length} sonomètre(s) sélectionné(s)
                </div>
              )}
            </div>
          )}

          {/* Color Scheme Selection */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Label>Schéma de couleurs</Label>
              <Select value={colorScheme} onValueChange={setColorScheme}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">🎨 Par défaut (Teal)</SelectItem>
                  <SelectItem value="ocean">🌊 Océan (Blue)</SelectItem>
                  <SelectItem value="forest">🌲 Forêt (Green)</SelectItem>
                  <SelectItem value="sunset">🌅 Coucher de soleil (Orange)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 flex items-end gap-2">
              <Button 
                onClick={loadChartData} 
                disabled={loading || (!selectedSoundmeter && chartType !== 'comparative')}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Générer
                  </>
                )}
              </Button>
              
              <Button 
                onClick={exportChartData}
                disabled={!chartData}
                variant="outline"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Info Alert */}
          {chartType === 'heatmap' && (
            <Alert>
              <AlertDescription>
                La carte de chaleur affiche les niveaux sonores sur une semaine (7 jours × 24 heures).
                Les couleurs indiquent l'intensité: vert (faible), orange (moyen), rouge (élevé).
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Chart Display */}
      {loading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <RefreshCw className="h-12 w-12 animate-spin text-teal-600" />
              <p className="text-slate-600">Génération du graphique en cours...</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        renderChart()
      )}
    </div>
  );
};

export default AdvancedGraphs;
